import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getTranslations, interpolate } from "./translations";

export const LANGUAGES = {
  EN: "en",
  PT_BR: "pt-BR",
};

export const LANGUAGE_PARAM = "lang";
const STORAGE_KEY = "language";

function normalize(value) {
  if (!value) return null;
  if (/^pt(?:-|$)/i.test(value)) return LANGUAGES.PT_BR;
  if (/^en(?:-|$)/i.test(value)) return LANGUAGES.EN;
  return null;
}

// The URL wins, so a shared link opens in the language it was shared in; then the reader's own
// last choice; then the browser.
function getInitialLanguage() {
  if (typeof window === "undefined") return LANGUAGES.EN;

  const fromUrl = normalize(new URLSearchParams(window.location.search).get(LANGUAGE_PARAM));
  if (fromUrl) return fromUrl;

  try {
    const stored = normalize(window.localStorage?.getItem(STORAGE_KEY));
    if (stored) return stored;
  } catch (error) {
    // Private browsing and blocked storage both throw; the browser preference still works.
  }

  return normalize(navigator?.language || navigator?.languages?.[0]) || LANGUAGES.EN;
}

function rememberLanguage(language) {
  try {
    window.localStorage?.setItem(STORAGE_KEY, language);
  } catch (error) {
    // Nothing to do: the choice simply will not survive this session.
  }
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
    rememberLanguage(language);
  }, [language]);

  const value = useMemo(() => ({
    language,
    locale: language === LANGUAGES.PT_BR ? "pt-BR" : "en-US",
    isPortuguese: language === LANGUAGES.PT_BR,
    translations: getTranslations(language),
    setLanguage,
  }), [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within a LanguageProvider");
  return context;
}

export function useTranslation() {
  const { translations, ...language } = useLanguage();
  const t = useCallback((path, variables) => {
    const value = path.split(".").reduce((current, key) => current?.[key], translations) ?? path;
    return typeof value === "string" ? interpolate(value, variables) : value;
  }, [translations]);

  return {
    ...language,
    t,
  };
}
